import React, {useEffect, useMemo, useState} from 'react';
import { useTranslation } from 'react-i18next';
import { OrbitControls } from "@react-three/drei";
import * as THREE from 'three'
import { Canvas } from "@react-three/fiber";
import { LightV1, fromRenderStructDTO } from '@3dcalc/3dpClient'

function BoardViewer({ url = 'board.json' }) {
    const [renderStruct, setRenderStruct] = useState(null);

    useEffect(() => {
        fetch(url)
            .then(r => r.json())
            .then(json => setRenderStruct(fromRenderStructDTO(json)))
            .catch(() => setRenderStruct(null));
    }, [url]);

    const elements = useMemo(() => {
        if (!renderStruct) return [];

        return renderStruct.elements.map((el, elIdx) => {
            const flatVerts = renderStruct.vertices[el.vertices];
            const flatInds  = renderStruct.indices[el.indices];

            if (!flatVerts?.length || !flatInds?.length) return null;

            const pos = el.position
                ? new THREE.Vector3(el.position[0], el.position[1], el.position[2])
                : new THREE.Vector3();

            const rot = el.rotation
                ? new THREE.Euler(el.rotation[0], el.rotation[1], el.rotation[2])
                : new THREE.Euler();

            const geo = new THREE.BufferGeometry();
            geo.setAttribute('position', new THREE.Float32BufferAttribute(flatVerts, 3));
            geo.setIndex(Array.from(flatInds));
            geo.computeVertexNormals();

            const edgesGeo = new THREE.EdgesGeometry(geo);

            const vertexPoints = [];
            for (let i = 0; i < flatVerts.length; i += 3) {
                vertexPoints.push(new THREE.Vector3(flatVerts[i], flatVerts[i + 1], flatVerts[i + 2]));
            }

            return { key: `el-${elIdx}`, geo, edgesGeo, vertexPoints, pos, rot };
        }).filter(Boolean);
    }, [renderStruct]);

    return (
        <>
            {elements.map(({ key, geo, edgesGeo, vertexPoints, pos, rot }) => (
                <group key={key} position={pos} rotation={rot}>
                    <mesh geometry={geo}>
                        <meshStandardMaterial
                            color="#10b981"
                            transparent
                            opacity={0.55}
                            side={THREE.DoubleSide}
                            depthWrite={false}
                        />
                    </mesh>

                    <lineSegments geometry={edgesGeo}>
                        <lineBasicMaterial color="#1a1a1a" />
                    </lineSegments>

                    {vertexPoints.map((v, i) => (
                        <mesh key={i} position={v}>
                            <sphereGeometry args={[0.01, 6, 6]} />
                            <meshBasicMaterial color="#ffffff" />
                        </mesh>
                    ))}
                </group>
            ))}
        </>
    );
}

const BeamViewer = () => (
    <div
        className="w-full bg-gray-950 rounded-2xl border border-gray-800 relative overflow-hidden"
        style={{ height: '480px' }}
    >
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-950/40 via-transparent to-transparent pointer-events-none z-10" />

        <Canvas
            style={{ width: '100%', height: '100%' }}
            camera={{ position: [-5, 6, 8], fov: 10 }}
            gl={{ antialias: true }}
        >
            <LightV1 />
            <group rotation={[Math.PI / 2, 0, Math.PI / 2]}>
                <BoardViewer />
            </group>
            <OrbitControls
                minPolarAngle={0}
                maxPolarAngle={Math.PI}
                enableDamping
                dampingFactor={0.05}
            />
        </Canvas>
    </div>
);

const BeamSection = () => {
    const { t } = useTranslation();

    const details = [
        { key: 'beam.detail1', icon: '◈' },
        { key: 'beam.detail2', icon: '◇' },
        { key: 'beam.detail3', icon: '◉' },
    ];

    const spec = t('beam.spec', { returnObjects: true });

    return (
        <section className="py-24 bg-gray-950 border-t border-gray-900">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">

                    <div className="order-2 lg:order-1">
                        <BeamViewer />
                    </div>

                    <div className="order-1 lg:order-2 flex flex-col gap-6">
            <span className="text-emerald-500 text-xs font-bold tracking-widest uppercase">
              {t('beam.label')}
            </span>

                        <h2 className="text-3xl sm:text-4xl font-bold text-white leading-tight">
                            {t('beam.title')}
                        </h2>

                        <p className="text-gray-400 text-lg leading-relaxed">
                            {t('beam.description')}
                        </p>

                        <ul className="flex flex-col gap-3 mt-2">
                            {details.map(({ key, icon }) => (
                                <li key={key} className="flex items-center gap-3">
                                    <span className="text-emerald-500 text-base shrink-0">{icon}</span>
                                    <span className="text-gray-300 font-medium">{t(key)}</span>
                                </li>
                            ))}
                        </ul>

                        {/* Спецификация */}
                        <div className="mt-2 rounded-xl bg-gray-900 border border-gray-800 overflow-hidden">
                            <div className="px-4 py-2.5 border-b border-gray-800 flex items-center justify-between">
                                <span className="text-gray-500 text-xs uppercase tracking-widest">{t('beam.specTitle')}</span>
                                <span className="text-emerald-500 text-xs font-mono">{t('beam.specAuto')}</span>
                            </div>
                            <table className="w-full font-mono text-sm">
                                <tbody>
                                {spec.map((row, i) => (
                                    <tr key={i} className="border-b border-gray-800/60 last:border-0">
                                        <td className="px-4 py-2 text-gray-300">{row.name}</td>
                                        <td className="px-4 py-2 text-gray-500 text-right whitespace-nowrap">{row.size}</td>
                                        <td className="px-4 py-2 text-amber-400 text-right w-12">{row.qty}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
};

export default BeamSection;
